import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SPONSORS } from "./sponsors.js";
import { SUPPORT_PATH, SUPPORT_SUBPAGE_PATH } from "../../pages/Support/paths.js";

/**
 * Tiered sponsor showcase — used on Über uns (dark page background) and on
 * the Sponsoring pitch page (light `tl-card-light`, § finding 8). `theme`
 * swaps text colors for the light card and gives Partner-tier logos (white-
 * on-transparent, no light variant on file) their own dark chip so they
 * stay visible off their native dark background — mirrors how the
 * Hauptförderer tile already forces its own `--color-cream` box regardless
 * of page theme. `showCta` is off on the Sponsoring page itself since the
 * CTA links to that very page. `title` overrides the default heading — the
 * Sponsoring page itself uses "Aktuell schon dabei" instead of the generic
 * "Sponsoren & Unterstützer", since visitors are already on that page.
 */
export default function SponsorShowcase({ lang, theme = "dark", showCta = true, title }) {
  const { t } = useTranslation();

  if (SPONSORS.length === 0) return null;

  const isLight = theme === "light";
  const headingColor = isLight ? "var(--color-ink)" : "var(--color-cream)";
  const bodyColor = isLight ? "rgba(10,9,7,0.85)" : "var(--color-cream-soft)";
  const partnerLabelColor = isLight ? "rgba(10,9,7,0.55)" : "var(--color-cream-faint)";

  const hauptfoerderer = SPONSORS.filter((s) => s.tier === "hauptfoerderer");
  const partner = SPONSORS.filter((s) => s.tier !== "hauptfoerderer");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: headingColor, margin: 0 }}>
          {title || t("sponsors.title")}
        </h2>
        <p style={{ fontSize: "15px", lineHeight: 1.7, color: bodyColor, maxWidth: "60ch", margin: 0 }}>
          {t("sponsors.aboutIntro")}
        </p>
      </div>

      {hauptfoerderer.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <span style={{ fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--color-gold)" }}>
            {t("sponsors.tierMain")}
          </span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
            {hauptfoerderer.map((sponsor) => (
              <a
                key={sponsor.name}
                href={sponsor.url}
                target="_blank"
                rel="noopener noreferrer"
                title={sponsor.name}
                className="tl-sponsor-logo-featured"
                style={{ justifyContent: "center", height: "130px", background: "var(--color-cream)", borderRadius: "12px" }}
              >
                <img
                  src={sponsor.logoLight ?? sponsor.logo}
                  alt={sponsor.name}
                  loading="lazy"
                  decoding="async"
                  width={Math.round((sponsor.logoWidth / 44) * 48)}
                  height={48}
                  style={{ height: "48px", width: "auto" }}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {partner.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <span style={{ fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", color: partnerLabelColor }}>
            {t("sponsors.tierPartner")}
          </span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
            {partner.map((sponsor) => (
              <a
                key={sponsor.name}
                href={sponsor.url}
                target="_blank"
                rel="noopener noreferrer"
                title={sponsor.name}
                className="tl-sponsor-logo"
                style={{
                  justifyContent: "center",
                  minHeight: "80px",
                  ...(isLight && { background: "var(--color-bg-raised)", borderRadius: "10px", padding: "12px" }),
                }}
              >
                <img
                  src={sponsor.logo}
                  alt={sponsor.name}
                  loading="lazy"
                  decoding="async"
                  width={Math.round((sponsor.logoWidth / 44) * 34)}
                  height={34}
                  style={{ height: "34px", width: "auto" }}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {showCta && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "32px",
            flexWrap: "wrap",
            border: "1px solid rgba(206,149,50,0.5)",
            borderRadius: "12px",
            padding: "26px 30px",
          }}
        >
          <p style={{ fontSize: "15px", lineHeight: 1.65, color: bodyColor, maxWidth: "60ch", margin: 0 }}>
            {t("sponsors.ctaBody")}
          </p>
          <Link
            to={`/${lang}/${SUPPORT_PATH[lang]}/${SUPPORT_SUBPAGE_PATH.sponsoring[lang]}`}
            className="tl-pill tl-pill-primary"
            style={{ padding: "14px 30px", flex: "none" }}
          >
            {t("sponsors.ctaLink")}
          </Link>
        </div>
      )}
    </div>
  );
}
